import {
  type MouseEvent,
  useCallback,
  useMemo,
  type JSX
} from 'react'
import {
  type ColumnDef,
  type Row as RowType
} from '@tanstack/react-table'

import {
  Table as _Table,
  TableBody,
  TableCell,
  TableRow
} from '@ttab/elephant-ui'
import { Toolbar } from './Toolbar'
import {
  useNavigation,
  useView,
  useTable,
  useHistory,
  useNavigationKeys,
  useOpenDocuments
} from '@/hooks'
import { handleLink } from '@/components/Link/lib/handleLink'
import { NewItems } from './NewItems'
import { LoadingText } from '../LoadingText'
import { Row } from './Row'
import { useModal } from '../Modal/useModal'
import { GroupedRows } from './GroupedRows'
import { type View } from '@/types/index'
import { useTranslation } from 'react-i18next'
const BASE_URL = import.meta.env.BASE_URL

interface TableProps<TData, TValue> {
  columns: Array<ColumnDef<TData, TValue>>
  type: 'Planning' | 'Event' | 'Assignments' | 'Search' | 'Factbox' | 'Print' | 'PrintEditor' | 'Timeless'
}

function getNextTableIndex(
  rows: Record<string, RowType<unknown>>,
  selectedRowIndex: number | undefined,
  direction: 'ArrowUp' | 'ArrowDown'): number | undefined {
  const keys = Object.keys(rows)
    .map(Number)
    .filter((numKey) => !isNaN(numKey))

  if (keys.length === 0) {
    return undefined
  }

  let currentIndex = selectedRowIndex !== undefined ? keys.indexOf(selectedRowIndex) : -1

  if (direction === 'ArrowDown') {
    if (currentIndex < keys.length - 1) {
      currentIndex += 1
    } else {
      return undefined
    }
  } else if (direction === 'ArrowUp') {
    if (currentIndex > 0) {
      currentIndex -= 1
    } else {
      return undefined
    }
  }

  return keys[currentIndex]
}

export const Table = <TData, TValue>({
  columns,
  type,
  searchType
}: TableProps<TData, TValue> & { searchType?: View }): JSX.Element => {
  const { state, dispatch } = useNavigation()
  const history = useHistory()
  const { viewId: origin } = useView()
  const { table, loading } = useTable()
  const openDocuments = useOpenDocuments({ idOnly: true })
  const { hideModal, currentModal } = useModal()
  const { t } = useTranslation()

  const handleOpen = useCallback((event: MouseEvent<HTMLTableRowElement> | KeyboardEvent, row: RowType<unknown>): void => {
    const target = event.target as HTMLElement
    if (target && 'dataset' in target && !target.dataset.rowAction) {
      const originalRow = row.original as { _id: string | undefined, id: string, fields?: Record<string, string[]> }
      const id = originalRow._id ?? originalRow.id

      const articleClick = type === 'Search' && searchType === 'Editor'

      let usableVersion

      if (articleClick) {
        usableVersion = !articleClick ? undefined : originalRow?.fields?.['heads.usable.version']?.[0] as bigint | undefined
      }

      handleLink({
        event,
        dispatch,
        viewItem: state.viewRegistry.get(!searchType ? type : searchType),
        props: { id },
        viewId: crypto.randomUUID(),
        origin,
        history,
        keepFocus: (event as KeyboardEvent)?.key === ' ',
        ...((articleClick && usableVersion) && {
          readOnly: {
            version: BigInt(usableVersion)
          }
        })
      })
    }
  }, [dispatch, state.viewRegistry, origin, type, history, searchType])

  useNavigationKeys({
    keys: ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', ' '],
    onNavigation: (event) => {
      const rows = table.getRowModel().rowsById
      if (!Object.values(rows)?.length) {
        return
      }

      const selectedRow = table.getGroupedSelectedRowModel()?.flatRows?.[0]

      if (event.key === 'Enter' && selectedRow) {
        hideModal()
        handleOpen(event, selectedRow)
        return
      }

      if (event.key === ' ' && selectedRow) {
        handleOpen(event, selectedRow)
        return
      }

      if (event.key === 'Escape') {
        selectedRow?.toggleSelected(false)
        return
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const nextKey = getNextTableIndex(rows, selectedRow?.index, event.key)
        if (nextKey !== undefined) {
          rows[nextKey].toggleSelected(true)
        }

        // Open next row if PreviewSheet is open
        if (currentModal && nextKey) {
          handleOpen(event, rows[nextKey])
        }

        return
      }
    }
  })

  const rows = table.getRowModel().rows
  const rowSelection = table.getState().rowSelection

  const TableBodyElement = useMemo(() => {
    if (loading || !rows?.length) {
      const isSearchTable = window.location.pathname.includes(`${BASE_URL}/search`)
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length}
            className='h-24 text-center'
          >
            <LoadingText>
              {loading
                ? isSearchTable
                  ? ''
                  : t('common:misc.loading')
                : t('errors:messages.noResultsFound')}
            </LoadingText>
          </TableCell>
        </TableRow>
      )
    }
    const isAssignmentsTable = window.location.pathname.includes(`${BASE_URL}/assignments`)

    return rows.map((row, index) => (
      table.getState().grouping.length)
      ? (
          <GroupedRows<TData, TValue>
            key={index}
            type={isAssignmentsTable ? 'Assignments' : type}
            row={row}
            columns={columns}
            handleOpen={handleOpen}
            openDocuments={openDocuments}
          />
        )
      : (
          <Row
            key={index}
            type={isAssignmentsTable ? 'Assignments' : type}
            row={row}
            handleOpen={handleOpen}
            openDocuments={openDocuments}
          />
        )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns, loading, handleOpen, table, rowSelection])

  return (
    <>
      {!['Factbox', 'Search'].includes(type) && (
        <Toolbar />
      )}
      {(type === 'Planning' || type === 'Event') && (
        <NewItems.Root>
          <NewItems.Table
            header={t('planning:yourNewType', { type: (type === 'Planning' ? t('event:subheadings.plannings') : t('event:subheadings.events')) })}
            type={type}
          />
        </NewItems.Root>
      )}

      {(type !== 'Search' || !loading) && (
        <_Table className='table-auto relative'>
          <TableBody>
            {TableBodyElement}
          </TableBody>
        </_Table>
      )}
    </>
  )
}
